import { designRefsComplete } from "./design-inputs";
import { isQuestionVisible, type QuestionCondition, type QuestionnaireAnswers } from "./questionnaire-conditions";
import { isMarketCurrency } from "./market-currency";

export interface RequiredAnswerItem {
  key: string;
  type: "confirm" | "voice_or_text" | "repeat_voice_or_text" | "text" | "choice" | "choice_or_text";
  minEntries?: number;
  questionVersion?: 1 | 2;
  required?: boolean;
  showIf?: QuestionCondition;
}

export interface RequiredAnswerValue {
  value: Record<string, unknown>;
  attachmentId?: string | null;
}

export function isRequiredAnswerComplete(
  item: RequiredAnswerItem,
  answer: RequiredAnswerValue | null | undefined,
  prefillFactCount = 1
): boolean {
  if (item.type === "confirm" && prefillFactCount === 0) return true;
  if (!answer) return false;
  const value = answer.value;
  if (answer.attachmentId) return true;
  if (item.type === "confirm") {
    return value.confirmed === true || value.reviewed === true ||
      (typeof value.correction === "string" && value.correction.trim().length > 0) ||
      (typeof value.text === "string" && value.text.trim().length > 0) ||
      (Array.isArray(value.links) && value.links.some((link) => typeof link === "string" && link.trim().length > 0));
  }
  // Legacy v1 q2_19 kept the note escape hatch: a v1 client mid-flight must
  // not be blocked by a rule introduced after their answer.
  if (item.key === "q1_13_profile_photo" || (item.key === "q2_19_sales_photos" && item.questionVersion === 1)) {
    const attachments = Array.isArray(value.attachments) ? value.attachments : [];
    const links = Array.isArray(value.links) ? value.links : [];
    return attachments.length > 0 || links.length > 0 ||
      (typeof value.text === "string" && value.text.trim().length > 0);
  }
  // F71: the photo question is a pure collection - done means at least one
  // photo or link, a note alone no longer counts.
  if (item.key === "q2_19_sales_photos") {
    // F71b: "nemam fotky" is an explicit, stored answer and completes the
    // question on its own - exactly like F87's defer_to_team on the design
    // question. Without it a client with no photos could never submit: F71
    // had removed the note escape hatch and left nothing in its place
    // (Tomas Pohl, Prenosilova 2026-09-04, 52 of 53 answered and stuck).
    if (value.no_photos === true) return true;
    const attachments = Array.isArray(value.attachments) ? value.attachments : [];
    const links = Array.isArray(value.links) ? value.links : [];
    return attachments.length > 0 || links.length > 0;
  }
  // F85: the proof question invites screenshots and links ("Klidně nahraj i
  // screenshoty..."), so an uploaded reference IS the answer. The value shape
  // always accepted embedded attachments/links here; only this rule demanded
  // prose on top and silently blocked the submit.
  if (item.key === "q2_06_proof") {
    const attachments = Array.isArray(value.attachments) ? value.attachments : [];
    const links = Array.isArray(value.links) ? value.links : [];
    return attachments.length > 0 || links.length > 0 ||
      (typeof value.text === "string" && value.text.trim().length > 0);
  }
  if (item.type === "voice_or_text") {
    return typeof value.text === "string" && value.text.trim().length > 0;
  }
  if (item.type === "repeat_voice_or_text") {
    const entries = Array.isArray(value.entries) ? value.entries : [];
    return entries.length >= (item.minEntries ?? 1) && entries.every((entry) => {
      if (!entry || typeof entry !== "object" || Array.isArray(entry)) return false;
      const row = entry as Record<string, unknown>;
      if (typeof row.id !== "string" || !row.id || typeof row.text !== "string" || !row.text.trim()) return false;
      return item.key !== "q2_43_use_cases" || row.real_vs_estimate === "real" || row.real_vs_estimate === "estimate";
    });
  }
  if (item.key.startsWith("q2_m")) {
    const selected = String(value.selected ?? "");
    if (!selected) return false;
    if (item.key === "q2_m02_socials" && selected === "community") {
      return typeof value.community_where === "string" && value.community_where.trim().length > 0;
    }
    if (item.key === "q2_m06_campaign" && selected === "yes") {
      return typeof value.campaign_type === "string" && value.campaign_type.trim().length > 0;
    }
    if (item.key === "q2_m07_payments" && selected === "no_stripe") {
      // F66: the order form link is no longer collected (q2_m08 carries the
      // client's pages); choosing the payment method completes the branch.
      const method = String(value.payment_method ?? "");
      if (!["fapi", "other_gateway", "none"].includes(method)) return false;
      if (method === "other_gateway" && !(typeof value.other_gateway === "string" && value.other_gateway.trim())) return false;
    }
    if (item.key === "q2_m08_promo" && selected === "yes") {
      return typeof value.materials === "string" && value.materials.trim().length > 0;
    }
    return true;
  }
  if (item.key === "q2_28_acquisition") {
    return Array.isArray(value.channels) && value.channels.length > 0 &&
      typeof value.budget === "string" && value.budget.trim().length > 0;
  }
  if (item.key === "q2_52_design_inputs") {
    // F76: any structured input completes the question; the normalizer reads
    // legacy values (style_source/inspiration) the same way.
    return designRefsComplete(value);
  }
  if (item.key === "q2_24_brand_face") {
    return ["owner", "standalone", "brand_recommend"].includes(String(value.mode));
  }
  if (item.key === "q1_07_buddy_name") {
    return typeof value.name === "string" && value.name.trim().length > 0;
  }
  if (item.key === "q1_02_twin_mode") {
    return value.mode === "clone" || value.mode === "knowledge_expert";
  }
  if (item.key === "q1_08_voice_and_meditation") {
    return (value.voice === "clone" || value.voice === "other") &&
      typeof value.meditation === "boolean";
  }
  if (item.key === "q2_12_audience_size") {
    const ranges = ["under_1000", "1000_4999", "5000_19999", "20000_plus"];
    return ranges.includes(String(value.email_range)) && ranges.includes(String(value.audience_range));
  }
  if (item.key === "q2_15_sales_model") {
    return ["two_day_trial", "webinar", "email_campaign", "inside_program", "recommend"]
      .includes(String(value.model));
  }
  if (item.key === "q2_10_price") {
    return value.mode === "recommend" || (
      value.mode === "range" &&
      isMarketCurrency(value.currency) &&
      Number.isInteger(value.min) &&
      Number.isInteger(value.max) &&
      Number(value.min) >= 0 &&
      Number(value.max) >= Number(value.min)
    );
  }
  if (item.key === "q2_16_stripe") return typeof value.has_stripe === "boolean";
  return false;
}

/**
 * F85: the ONE derivation of "which required questions still block the
 * submit". The runner's headline, hint and button AND the worker's submit
 * assertion all consume this list; a surface must never re-filter required
 * items on its own (two derivations is exactly how the green "Máš hotovo"
 * ended up above a disabled submit button).
 */
export function missingRequiredAnswers<T extends RequiredAnswerItem>(
  items: readonly T[],
  values: QuestionnaireAnswers,
  answer: (item: T) => RequiredAnswerValue | null | undefined,
  prefillFactCount: (item: T) => number = () => 1
): T[] {
  return items.filter((item) => {
    if (!item.required) return false;
    if (!isQuestionVisible(item, values)) return false;
    return !isRequiredAnswerComplete(item, answer(item), prefillFactCount(item));
  });
}
